import { useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "@/integrations/firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/shared/services/ErrorReporting";
import type { Character } from "@/types/summary";

function isProtagonist(role: string): boolean {
  return role.toLowerCase() === "protagonist";
}

export interface UseCharactersResult {
  characters: Character[] | null;
  isLoading: boolean;
  error: Error | null;
}

export const useCharacters = (
  summaryId: string | null,
): UseCharactersResult => {
  const { authUser } = useAuth();
  const [characters, setCharacters] = useState<Character[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!authUser || !summaryId) {
      setCharacters(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);

    const charactersCollection = collection(
      db,
      "summaries",
      summaryId,
      "characters",
    );

    const unsubscribe = onSnapshot(
      charactersCollection,
      (snapshot) => {
        if (snapshot.empty) {
          setCharacters(null);
          setIsLoading(false);
          return;
        }

        const extracted: Character[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            name: data.name ?? "",
            age: data.age ?? "",
            pronouns: data.pronouns ?? "",
            role: data.role ?? "",
            description: data.description ?? "",
            aliases: data.aliases ?? [],
          };
        });

        extracted.sort(
          (a, b) =>
            Number(isProtagonist(b.role)) - Number(isProtagonist(a.role)),
        );
        setCharacters(extracted);
        setIsLoading(false);
      },
      (err) => {
        logError(err);
        setError(err);
        setCharacters(null);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [authUser, summaryId]);

  return { characters, isLoading, error };
};
